import type { GetLoginLinkResponseErrors } from "./sdk-client";

import { Sdk } from "./sdk-client";
import type {
  GamePlayer,
  OnboardingAnalyticsEvent,
  ScheduleNotificationV2Message,
} from "@textclub/common";

/**
 * Defines the public instance methods shared by the Sdk class and the Textclub singleton.
 */
export interface TextclubSDK {
  /**
   * Waits for the SDK to be fully initialized with player data.
   * @returns A promise that resolves when the player is set.
   * @throws {Error} If initialization times out or the SDK is not initialized.
   */
  isReady(): Promise<void>;

  /**
   * Returns the payload associated with this entry into the game.
   *
   * The payload is included in the link that the player entered the game from and is attached by the sender.
   * That can be a reminder link sent to the player's inbox or a referral link shared with a friend.
   * It can also be attached by the onboarding game associated with the title, if such exists.
   *
   * @returns The entry payload as a JSON object.
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * const payload = textclub.getEntryPayload();
   * console.log(payload.referralCode);
   * ```
   */
  getEntryPayload(): Record<string, unknown>;

  /**
   * Gets player info
   * @returns The player object
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * const player = sdk.getPlayer();
   * console.log(player.playerId);
   * ```
   * */
  getPlayer(): GamePlayer;

  /**
   * Gets a state value from the local state
   * @param key - The state key to retrieve
   * @returns The state value, or undefined if not found
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * const score = sdk.getPlayerDataVal("score");
   * console.log(score);
   * ```
   */
  getPlayerDataVal(key: string): unknown;

  /**
   * Retrieves the current player state.
   * @returns The current player state object.
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * const state = sdk.getPlayerData();
   * console.log(state.score);
   **/
  getPlayerData(): Record<string, unknown>;

  /**
   * Sets a single state value
   * @param key - The state key to set
   * @param value - The value to store
   * @returns void
   * @throws {Error} If the player is not initialized.
   * @example
   * ```typescript
   * sdk.setStateVal("score", 100);
   * ```
   */
  setPlayerDataVal(key: string, value: unknown): void;

  /**
   * Sets multiple state values
   * @param partial - The partial state object to set
   * @returns void
   * @throws {Error} If the player is not initialized.
   *
   * @example
   * ```typescript
   * sdk.setPlayerData({ score: 100, level: 2 });
   * ```
   */
  setPlayerData(partial: Record<string, unknown>): void;

  /**
   * Initiates a login flow with a registration code
   * @param opts - Configuration for the login prompt.
   * @param opts.message - The message containing `{{registrationCode}}`.
   * @param opts.title - The title of the login prompt.
   * @param opts.entryPayload - Optional additional data to include in the entry payload. This will be accessible as `getEntryPayload()` after login.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  login(opts: {
    message: string;
    title: string;
    entryPayload?: Record<string, unknown>;
  }): void;

  /**
   * Create a direct link to a game without requiring login
   * @param options.gameSlug - The slug of the game to link to
   * @param options.entryPayload - Optional additional data to include in the entry payload. This will be accessible as `getPlayer().entryPayload` after accessing the link.
   * @returns A promise that resolves to the generated link URL
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   * @example
   * ```typescript
   * const link = await sdk.getLink({ gameSlug: "onboarding-game" });
   * console.log(`Access the game at: ${link}`);
   * ```
   */
  getLink(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): Promise<GetLinkResponse>;

  /**
   * Schedules a notification for the player.
   *
   * @param options - Notification details.
   * @param options.scheduledAt - When to send the notification.
   * @param options.message - The notification content.
   * @param options.attemptPushNotification - Whether to try a push notification.
   * @param options.identifier - Identifier of the notification.
   * If another notification with the same identifier is scheduled, the
   * previous is unscheduled. This ensures that only one notification of the
   * same identifier is scheduled. Once the notification is delivered, another
   * one can be scheduled for the same identifier.
   *
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  scheduleNotification(options: {
    scheduledAt: Date;
    message: string;
    attemptPushNotification: boolean;
    identifier?: string;
  }): void;

  /**
   * Unschedules a previously scheduled notification using the identifier.
   * @param options - Unschedule options.
   * @param options.identifier - The identifier used when scheduling the notification.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  unscheduleNotification(options: { identifier: string }): void;

  /**
   * Captures an analytics event.
   * @param eventName - The name of the event.
   * @param properties - Optional additional event data.
   * @throws {Error} If the SDK is not initialized or arguments are invalid.
   */
  captureEvent(eventName: string, properties?: Record<string, unknown>): void;

  /**
   * Redirects the player from the onboarding game to the linked flagship game.
   *
   * If the current game is not configured as an onboarding game, a message is
   * alerted and the nothing happens.
   *
   * @param options.entryPayload - Optional additional data to include in the entry payload for the flagship game.
   *
   * @throws {INVALID_ARGUMENTS} If entryPayload is not a valid object.
   * @throws {Error} If the SDK is not initialized.
   */
  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void;

  /**
   * Redirects the player to the specified game.
   *
   * @param options.gameSlug - The slug of the game to redirect to.
   * @param options.entryPayload - Optional additional data to include in the entry payload for the target game.
   *
   * @throws {INVALID_ARGUMENTS} If gameSlug is not a valid string or entryPayload is not a valid object.
   * @throws {Error} If the SDK is not initialized.
   */
  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): void;

  /**
   * Opens the privacy policy page in a new window.
   * @example
   * ```typescript
   * sdk.openPrivacyPolicy();
   * ```
   */
  openPrivacyPolicy(): void;
  /**
   * Opens the terms of service page in a new window.
   * @throws {Error} If the SDK is not initialized.
   */
  openTermsOfService(): void;
  /**
   * Opens the copyright page in a new window.
   * @throws {Error} If the SDK is not initialized.
   */
  openCopyright(): void;

  /**
   * Ensures any state updates up to this point have been saved by the server
   * @returns Promise<void>
   * @throws {Error} If the SDK is not initialized.
   * @example
   * ```typescript
   * sdk.setPlayerData({ score: 100 });
   * await sdk.flush();
   * ```
   * */
  flush(): Promise<void>;

  /**
   * Namespaced out notification api
   */
  notifications: {
    /**
     * Schedules a notification with rich content including images.
     * @param options - Notification details.
     * @param options.scheduledAt - When to send the notification.
     * @param options.image - Base64 data URL for the notification image (e.g., "data:image/png;base64,iVBORw0...").
     * @param options.fallbackBody - Fallback text for when the notification cannot display rich content.
     * @param options.body - Main body text of the notification.
     * @param options.ctaText - Call-to-action button text.
     * @param options.priority - Priority level of the notification ("low", "medium", or "high"). Defaults to "low".
     * @param options.identifier - Unique identifier for the notification.
     * @param options.entryPayload - Optional payload data accessible when the notification is clicked.
     * @throws {Error} If the SDK is not initialized or arguments are invalid.
     * @example
     * ```typescript
     * textclub.v2.scheduleNotification({
     *   scheduledAt: new Date(Date.now() + 60000), // 1 minute from now
     *   image: "data:image/png;base64,iVBORw0KGgoAAAANS...",
     *   fallbackBody: "You have a new message!",
     *   body: "Check out the latest updates in the game",
     *   ctaText: "Play Now",
     *   priority: "high",
     *   identifier: "daily-reminder"
     * });
     * ```
     */
    scheduleNotification(options: {
      scheduledAt: Date;
      image: string;
      fallbackBody: string;
      body: string;
      ctaText: string;
      priority: ScheduleNotificationV2Message["priority"];
      identifier?: string;
      entryPayload?: Record<string, unknown>;
    }): void;

    /**
     * Unschedules a previously scheduled notification using the identifier.
     * @param options - Unschedule options.
     * @param options.identifier - The identifier used when scheduling the notification.
     * @throws {Error} If the SDK is not initialized or arguments are invalid.
     */
    unscheduleNotification(options: { identifier: string }): void;
  };

  /**
   * Payments API namespace for in-app purchases
   */
  payments: {
    /**
     * Retrieves the list of available products for the current game.
     * @returns A promise that resolves with an array of products.
     * @throws {Error} If the SDK is not initialized or the request fails.
     * @example
     * ```typescript
     * const products = await textclub.payments.getProducts();
     * products.forEach(product => {
     *   console.log(`${product.name}: $${product.price / 100}`);
     * });
     * ```
     */
    getProducts(): Promise<
      Array<{
        sku: string;
        name: string;
        description: string | null;
        price: number;
      }>
    >;

    /**
     * Begins a platform purchase flow for the specified product.
     * @param options - Purchase configuration.
     * @param options.productSku - The ID of the product to purchase.
     * @returns A promise that resolves with the purchase result containing a purchase token on success.
     * @throws {Error} If the SDK is not initialized or arguments are invalid.
     * @example
     * ```typescript
     * const result = await textclub.payments.beginPurchase({
     *   productSku: "powerup-pack-1",
     * });
     * if (result.result === "success") {
     *   console.log('Purchase completed with token:', result.purchaseToken);
     * }
     * ```
     */
    beginPurchase(options: { productSku: string }): Promise<
      | { result: "success"; purchaseToken: string }
      | { result: "cancel" }
      | {
          result: "error";
          error: "internal_error" | "invalid_product" | "purchase_failed";
        }
    >;
  };

  internal: {
    /**
     * Acquires a lease for a login message that will be reserved for this user.
     * The returned reservation object is then later used with `sendReservedLoginMessage` to trigger sending an sms.
     *
     * This is done to ensure that the message is associated with the current user and will be correctly attributed.
     *
     * @param options.message - The message to acquire a lease for.
     * @param options.entryPayload - Optional additional data to include in the entry payload. This will be accessible as `getEntryPayload()` after login.
     * @returns A promise that resolves to either:
     *   - `{ reservation: LoginReservation }` - An opaque reservation object that can be used with `sendReservedLoginMessage` to send the SMS.
     *   - `{ error: "could_not_acquire_lease" }` - Could not acquire a login lease. This happens when an active login lease already exists for the given message.
     * @throws {Error} If the SDK is not initialized or arguments are invalid.
     *
     * @example
     * ```typescript
     * const response = await textclub.internal.reserveLoginMessageAsync({ message: "Your code is abc123" });
     * if ("reservation" in response) {
     *   textclub.internal.sendReservedLoginMessage(response.reservation); // trigger a login sms.
     * } else {
     *   console.error(`Failed to create reservation: ${response.error}`);
     * }
     *
     * @internal
     * ```
     */
    reserveLoginMessageAsync(options: {
      message: string;
      keywords?: string[];
      entryPayload?: Record<string, unknown>;
    }): Promise<GetLoginTokenResponse>;

    /**
     * Triggers an SMS link to open the user's messaging app prefilled with the
     * message reserved with `reserveLoginMessageAsync`.
     *
     * @param reservation - The reservation object returned by `reserveLoginMessageAsync`.
     *
     * @internal
     */
    sendReservedLoginMessage(reservation: LoginReservation): void;

    /**
     * Tracks when an SMS modal is shown to the user.
     * This emits a platform-level analytics event.
     *
     * @throws {Error} If the SDK is not initialized.
     *
     * @internal
     */
    captureOnboardingEvent(event: OnboardingAnalyticsEvent): void;
  };
}

let sdkInstance: Sdk | null = null;

/**
 * Ensures the SDK instance is initialized.
 * @throws {Error} If the SDK has not been initialized by calling Textclub.init().
 */
function ensureInstance(): Sdk {
  if (!sdkInstance) {
    throw new Error("SDK not initialized. Call Textclub.init() first.");
  }
  return sdkInstance;
}

export type GetLoginLinkResponse =
  | {
      link: string;
    }
  | {
      error: typeof GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE;
    };

export type LoginReservation = {
  readonly __type: "LoginReservation";
  readonly __token: string;
};

export type GetLoginTokenResponse =
  | {
      reservation: LoginReservation;
    }
  | {
      error: typeof GetLoginLinkResponseErrors.COULD_NOT_ACQUIRE_LEASE;
    };

export type GetLinkResponse = {
  link: string;
};

/**
 * The main SDK singleton for integrating games with the Textclub platform.
 * Handles player management, notifications, and analytics within an iframe context.
 *
 * @example
 * ```typescript
 * import Textclub from '@textclub/client';
 *
 * async function main() {
 *   await Textclub.init();
 *   console.log('Initialization complete');
 *   const player = Textclub.getPlayer();
 *   console.log('Player ID:', player.playerId);
 *   Textclub.setPlayerDataVal('score', 100);
 * }
 *
 * main();
 * ```
 */
// Define the type for the singleton, combining the shared interface with static-like members
export type Textclub = TextclubSDK & {
  init: (opts?: {
    initialPlayerData?: Record<string, unknown>;

    /**
     * @deprecated mock is no longer used - it is automatically determined based on whether the app is run inside Textclub or as standalone.
     */
    mock?: boolean;
  }) => Promise<void>;
};

export const textclub: Textclub = {
  /**
   * Initializes the SDK. Must be called before any other SDK methods.
   * If already initialized, it returns the existing ready promise.
   * @param opts - Configuration options for the SDK.
   * @param opts.initialPlayerData - The initial player state to use if player is uninitialized. Defaults to an empty object.
   * @param opts.mock - Whether to use a mock SDK for testing. Includes a debug menu for simulating player states and interactions. Defaults to false.
   * @returns A promise that resolves when the SDK is ready and player data is available.
   */
  init(
    opts: { initialPlayerData?: Record<string, unknown>; mock?: boolean } = {},
  ) {
    if (!sdkInstance) {
      // Determine if it's mock based on whether tce=1 is in the querystring.
      const params = new URLSearchParams(window?.location?.search);
      const isOnTextclub = params.get("tce") === "1";

      if (!isOnTextclub) {
        sdkInstance = Sdk.mock(opts);
      } else {
        sdkInstance = new Sdk(opts);
      }
    }

    return sdkInstance.isReady();
  },

  getEntryPayload(): Record<string, unknown> {
    return ensureInstance().getEntryPayload();
  },

  isReady(): Promise<void> {
    return ensureInstance().isReady();
  },

  getPlayer(): GamePlayer {
    return ensureInstance().getPlayer();
  },

  getPlayerDataVal(key: string): unknown {
    return ensureInstance().getPlayerDataVal(key);
  },

  getPlayerData(): Record<string, unknown> {
    return ensureInstance().getPlayerData();
  },

  setPlayerDataVal(key: string, value: unknown): void {
    ensureInstance().setPlayerDataVal(key, value);
  },

  setPlayerData(partial: Record<string, unknown>): void {
    ensureInstance().setPlayerData(partial);
  },

  login: (opts: {
    message: string;
    title: string;
    entryPayload?: Record<string, unknown>;
  }): void => {
    ensureInstance().login(opts);
  },

  getLink: (options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): ReturnType<Sdk["getLink"]> => {
    return ensureInstance().getLink(options);
  },

  scheduleNotification: (options: {
    scheduledAt: Date;
    message: string;
    attemptPushNotification: boolean;
    identifier?: string;
  }): void => {
    ensureInstance().scheduleNotification(options);
  },

  unscheduleNotification: (options: { identifier: string }): void => {
    ensureInstance().unscheduleNotification(options);
  },

  captureEvent: (
    eventName: string,
    properties?: Record<string, unknown>,
  ): void => {
    ensureInstance().captureEvent(eventName, properties);
  },

  redirectToFlagshipGame(options?: {
    entryPayload?: Record<string, unknown>;
  }): void {
    ensureInstance().redirectToFlagshipGame(options);
  },

  redirectToGame(options: {
    gameSlug: string;
    entryPayload?: Record<string, unknown>;
  }): void {
    ensureInstance().redirectToGame(options);
  },

  openPrivacyPolicy(): void {
    ensureInstance().openPrivacyPolicy();
  },

  openTermsOfService(): void {
    ensureInstance().openTermsOfService();
  },

  openCopyright(): void {
    ensureInstance().openCopyright();
  },

  flush(): Promise<void> {
    return ensureInstance().flush();
  },

  notifications: {
    scheduleNotification: (options): void => {
      ensureInstance().notifications.scheduleNotification(options);
    },

    unscheduleNotification: (options): void => {
      ensureInstance().notifications.unscheduleNotification(options);
    },
  },

  payments: {
    getProducts: () => {
      return ensureInstance().payments.getProducts();
    },

    beginPurchase: (options) => {
      return ensureInstance().payments.beginPurchase(options);
    },
  },

  internal: {
    reserveLoginMessageAsync: (options: {
      message: string;
      keywords?: string[];
      entryPayload?: Record<string, unknown>;
    }): Promise<GetLoginTokenResponse> => {
      return ensureInstance().internal.reserveLoginMessageAsync(options);
    },

    sendReservedLoginMessage: (reservation: LoginReservation) => {
      return ensureInstance().internal.sendReservedLoginMessage(reservation);
    },

    captureOnboardingEvent: (event: OnboardingAnalyticsEvent) => {
      return ensureInstance().internal.captureOnboardingEvent(event);
    },
  },
};

// Also export the Sdk class for direct instantiation if needed
export { Sdk };

// Re-export relevant types
export type { GamePlayer };
